<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import SubAssetsRepository from '@repositories/subAssets.repository';
import { IAsset } from '../../types/assets.type';
import AssetsRepository from '@repositories/assets.repository';
import { ISubAsset } from '../../types/subAssets.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingSubAssets = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { subAssets: subAssetsPinia }: any = storeToRefs(store);
const subAssets = ref<ISubAsset[]>([]);
const assets = ref<IAsset[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedSubAsset = ref<ISubAsset | null>(null);
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Sub-Assets',
  },
  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Sub-Assets',
  },
  {
    key: 'riskType',
    label: 'Riesgo',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'asset',
    label: 'Asset',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'indemnificationLimitMin',
    label: t('global.limite_minimo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'indemnificationLimitMax',
    label: t('global.limite_maximo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'assetInfo',
    label: 'Editar',
  },
  {
    key: 'delete',
    label: 'Eliminar',
  },
]);
const fieldsFilter: any = ref([
  {
    key: 'search',
    title: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por descripcion del sub-asset',
    value: null,
    cols: 3,
  },
  {
    key: 'assetId',
    title: 'Nombre del Asset',
    type: 'select',
    label: 'description',
    placeholder: 'Buscar por asset',
    formatValue: (val: any) => val?.id ?? null,
    value: null,
    options: assets,
    cols: 3,
  },
  {
    key: 'riskType',
    title: 'Tipo de riesgo',
    label: 'description',
    type: 'select',
    placeholder: 'Buscar por tipo de riesgo',
    value: null,
    formatValue: (val: any) => val?.description ?? null,
    options: [
      { id: 1, description: 'LOW' },
      { id: 2, description: 'MEDIUM' },
      { id: 3, description: 'HIGH' },
    ],
    cols: 3,
  },
]);
const searchFormRef = ref<any>(null);

// API
const getSubAssetsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingSubAssets.value = true;
  try {
    const data = await SubAssetsRepository.getSubAssets({
      page,
      deleted,
      ...params,
    });

    if (data && Array.isArray(data?.subAssets)) {
      paginationData.value = data.pagination;
      updateSubAssets(data.subAssets);
      return data.subAssets;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching subAssets:', error);
    return [];
  } finally {
    isLoadingSubAssets.value = false;
  }
};

const getAssetsFromApi = async () => {
  const { data } = await AssetsRepository.getAssetsPublic();

  assets.value = data;
};

const updateSubAssets = (newSubAssets: ISubAsset[]) => {
  subAssets.value = newSubAssets;
  store.setProp({ key: 'subAssets', value: newSubAssets });
};

const loadSubAssets = async () => {
  await getSubAssetsFromAPI(1);
};

onMounted(() => {
  loadSubAssets();
  getAssetsFromApi();
});

// Create inputs from modals
const fieldsInputSubAsset = computed(() => {
  return [
    {
      key: 'description',
      title: 'Nombre del Asset',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Nuevo sub asset',
      value: selectedSubAsset.value?.description,
      cols: 12,
    },
    {
      key: 'assetId',
      label: 'description',
      selectLabel: null,
      value: selectedSubAsset.value?.asset.id,
      searchable: true,
      title: 'Asset',
      trackBy: 'id',
      type: 'select',
      placeholder: 'Nombre del asset',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: assets,
    },
    {
      key: 'indemnificationLimitMin',
      value: selectedSubAsset.value?.indemnificationLimitMin,
      title: t('global.limite_minimo'),
      type: 'text',
      placeholder: t('global.limite_minimo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'indemnificationLimitMax',
      value: selectedSubAsset.value?.indemnificationLimitMax,
      title: t('global.limite_maximo'),
      type: 'text',
      placeholder: t('global.limite_maximo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'riskTypeId',
      label: 'description',
      value: selectedSubAsset.value?.riskType.id,
      searchable: true,
      title: 'Riesgo',
      type: 'select',
      trackBy: 'id',
      placeholder: 'Tipo de riesgo',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getSubAssetsFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Agregar sub asset';
  isModalOpen.value = true;
  modalType.value = 'modalCreateSubAsset';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedSubAsset.value = null;
};

const handleActive = async () => {
  const subAssets = await getSubAssetsFromAPI(1);
  updateSubAssets(subAssets);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const subAssetsDeleted = await getSubAssetsFromAPI(1, true);
  updateSubAssets(subAssetsDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ISubAsset) => {
  modalTitle.value = isViewActive.value
    ? `Borrar el sub asset con id: ${item.id}`
    : `Restaurar el sub asset con id: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedSubAsset.value = item;
  isModalOpen.value = true;
};

// Filter Functions

const handleOnSubmit = async ({ items }: any) => {
  getSubAssetsFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getSubAssetsFromAPI(1, false);
};

//CRUD
const handleCreateSubAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await SubAssetsRepository.createSubAsset(items);
    store.setProp({ key: 'subAssets', value: [...subAssets.value, data] });
	subAssets.value = [...subAssets.value, data];
    $toast?.success(`Sub asset agregado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar crear');
  }

  handleClose();
};

const handleSelectSubAsset = (subAssetData: ISubAsset) => {
  modalTitle.value = 'Actualizar sub Asset';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateSubAsset');
  selectedSubAsset.value = subAssetData;
};

const handleUpdateSubAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedSubAsset.value) return;
  const assetForUpdate = {
    ...items,
    id: selectedSubAsset.value.id,
  };

  try {
    const data = await SubAssetsRepository.updateSubAsset(assetForUpdate);
    let index = store.subAssets.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'subAssets', index, value: data });
    }
    $toast?.success(`Sub asset actualizado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar actualizar');
  }

  handleClose();
};

const handleDeleteRestoreSubAsset = async () => {
  if (selectedSubAsset?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await SubAssetsRepository.deleteSubAsset(
          selectedSubAsset.value.id,
        );
      } else {
        response = await SubAssetsRepository.restoreSubAsset(
          selectedSubAsset.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'subAssets',
          value: selectedSubAsset?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error('Error al intentar eliminar');
      }
      $toast?.error('Error al intentar restaurar');
    }

    handleClose();
  }
};
</script>

<template>
  <Layout title="Sub-Assets" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>Activos</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>Inactivos</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> Agregar sub asset </template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingSubAssets"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="subAssetsPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(assetInfo)="assetData">
          <div
            @click="handleSelectSubAsset(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateSubAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputSubAsset"
            @onSubmit="handleCreateSubAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar sub asset</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateSubAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputSubAsset"
            @onSubmit="handleUpdateSubAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Actualizar</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el sub asset ${selectedSubAsset?.description}?`
                : `¿Está seguro que desea restaurar el sub asset ${selectedSubAsset?.description}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreSubAsset"
            >
              <template #content>
                <div>
                  <span>Confirmar</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
